import React from 'react';
import { Button, Spinner } from 'tuui';
import { ReloadIcon } from 'tuui/lib/atoms/Icon/Icons';
import { IndicationContent, INDICATION_TYPES } from 'taboola-ultimate-ui';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useConversions } from '../../hooks/useConversions';
import { useSaveAndClose } from '../../hooks/useSaveAndClose';
import { ReviewEventCard } from './ReviewEventCard';
import styles from './reviewEvents.module.scss';

export const ReviewEvents = ({ goBack, onClose, setFocusedConversionId }) => {
    const { touchedConversions } = useConversions();

    const {
        isLoading,
        showErrorModal,
        showSuccessModal,
        errorMessage,
        handleSaveAndClose,
        hideErrorModal,
        hideSuccessModal,
    } = useSaveAndClose({ touchedConversions, onClose });

    return (
        <>
            <div className={styles['container']}>
                <div className={styles['title']}>
                    <FormattedMessage id="codeless.review.event.header" defaultMessage="Review Changes" />
                </div>
                <div className={styles['header-subtitle']}>
                    <FormattedMessage
                        id="codeless.review.event.subtitle.header"
                        defaultMessage="Event Created and edited"
                    />
                </div>
            </div>
            <div className={styles['review-cards-container']}>
                {touchedConversions.map(event => (
                    <ReviewEventCard event={event} key={event.id} setFocusedConversionId={setFocusedConversionId} />
                ))}
            </div>
            <footer className={styles['footer']}>
                <Button
                    onClick={goBack}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Back"
                    disabled={isLoading}
                >
                    <FormattedMessage id="codeless.back.button" defaultMessage="Back" />
                </Button>
                <div className={styles['save-button-container']}>
                    <Button
                        onClick={handleSaveAndClose}
                        variant={Button.variant.primary}
                        size={Button.size.md}
                        aria-label="Save & close"
                        disabled={isLoading || touchedConversions.length === 0}
                    >
                        {isLoading && <Spinner size={16} className={styles['spinner']} />}
                        <FormattedMessage id="codeless.save.and.close.button" defaultMessage="Save & close" />
                    </Button>
                </div>
            </footer>

            {isLoading && (
                <div className={styles['central-loading-container']}>
                    <div className={styles['central-loading-content']}>
                        <Spinner size={32} className={styles['central-spinner']} />
                        <span className={styles['central-loading-text']}>
                            <FormattedMessage id="codeless.saving.label" defaultMessage="Saving your events" />
                        </span>
                    </div>
                </div>
            )}

            {showErrorModal && (
                <div className={styles['modal-overlay']} onClick={hideErrorModal}>
                    <div className={styles['modal-content']} onClick={e => e.stopPropagation()}>
                        <div className={styles['container']}>
                            <div className={styles['title']}>
                                <FormattedMessage id="codeless.review.event.header" defaultMessage="Review Changes" />
                            </div>
                        </div>
                        <div className={styles['error-indication']}>
                            <IndicationContent
                                type={INDICATION_TYPES.ERROR}
                                contentClassName={styles['error-indication-message']}
                                displayIcon
                            >
                                {errorMessage}
                            </IndicationContent>
                        </div>
                        <div className={styles['error-actions']}>
                            <Button
                                onClick={() => {
                                    hideErrorModal();
                                    handleSaveAndClose();
                                }}
                                className={styles['error-retry-button']}
                                size={Button.size.md}
                            >
                                <ReloadIcon className={styles['retry-icon']} />
                                <FormattedMessage id="codeless.retry.button" defaultMessage="Try Again" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div className={styles['modal-overlay']} onClick={hideSuccessModal}>
                    <div className={styles['modal-content']} onClick={e => e.stopPropagation()}>
                        <div className={styles['success-indication']}>
                            <IndicationContent
                                type={INDICATION_TYPES.SUCCESS}
                                contentClassName={styles['success-indication-message']}
                                displayIcon
                            >
                                <FormattedMessage
                                    id="codeless.save.and.close.success"
                                    defaultMessage="Codeless rules were saved successfully"
                                />
                            </IndicationContent>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
